import { ReactNode } from 'react';
import { MenuItem, Select, Stack } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  MetricFormat,
  MetricPerformanceFactor,
  SCORECARD_COMMON_GOAL_TYPE_REGULAR,
  SCORECARD_GOAL_TYPE_METRIC,
} from 'constants/scorecards';
import { TooltipInfoIcon, TooltipWithIcon } from 'components/Select';
import { SelfAssessmentSetups } from 'helpers';
import { FieldGroup, FieldLabel } from './GoalEditor.styled';
import { FormValues } from './interfaces';

interface GoalTypeSelectProps {
  goalIndex: number;
  disabled?: boolean;
  className?: string;
}

interface GoalTypeOption {
  id: string;
  label: string;
  tooltip: ReactNode;
}

const goalTypeOptions: GoalTypeOption[] = [
  {
    id: SCORECARD_COMMON_GOAL_TYPE_REGULAR,
    label: 'Feedback',
    tooltip: (
      <FormattedMessage
        id="forms.goalType.feedback.tooltip"
        defaultMessage="Standard feedback-based goals for team assessment"
      />
    ),
  },
  {
    id: SCORECARD_GOAL_TYPE_METRIC,
    label: 'Metric',
    tooltip: (
      <FormattedMessage
        id="forms.goalType.metric.tooltip"
        defaultMessage="Goals based on measurable metrics and targets"
      />
    ),
  },
];

export default function GoalTypeSelect({
  goalIndex,
  disabled,
  className,
}: GoalTypeSelectProps) {
  const { control, getValues, setValue, clearErrors, trigger } =
    useFormContext<FormValues>();
  const { formatMessage } = useIntl();
  const placeholder = formatMessage({
    id: 'pages.scorecards.edit.goalTypePlaceholder',
    defaultMessage: 'Goal type',
  });

  const handleTypeChange = (
    value: string,
    fieldOnChange: (next: string) => void,
  ) => {
    fieldOnChange(value);

    if (value === SCORECARD_GOAL_TYPE_METRIC) {
      const subgoalsPath = `goals.${goalIndex}.subgoals` as const;
      const subgoals = getValues(subgoalsPath) || [];
      subgoals.forEach((subgoal, index) => {
        const base = `${subgoalsPath}.${index}` as const;
        if (!subgoal?.metricFormat) {
          setValue(`${base}.metricFormat`, MetricFormat.NUMBER, {
            shouldDirty: true,
          });
        }
        if (!subgoal?.metricPerformanceFactor) {
          setValue(
            `${base}.metricPerformanceFactor`,
            MetricPerformanceFactor.PRORATED,
            { shouldDirty: true },
          );
        }
        if (subgoal?.metricTarget == null) {
          setValue(`${base}.metricTarget`, 0, { shouldDirty: true });
        }
      });

      const goalPath = `goals.${goalIndex}` as const;
      setValue(
        `${goalPath}.selfAssessmentSetup`,
        SelfAssessmentSetups.disable,
        {
          shouldDirty: true,
        },
      );
      setValue(`${goalPath}.allowScorecardMetricPmOwnerEdits`, true, {
        shouldDirty: true,
      });
      setValue(`${goalPath}.allowScorecardOwnerEditActualField`, true, {
        shouldDirty: true,
      });
    } else if (value === SCORECARD_COMMON_GOAL_TYPE_REGULAR) {
      setValue(
        `goals.${goalIndex}.selfAssessmentSetup`,
        SelfAssessmentSetups.request,
        { shouldDirty: true },
      );
      setValue(`goals.${goalIndex}.allowScorecardOwnerEdits`, true, {
        shouldDirty: true,
      });
    }

    clearErrors(`goals.${goalIndex}`);
    setTimeout(() => trigger(`goals.${goalIndex}`), 0);
  };
  return (
    <FieldGroup className={className}>
      <FieldLabel htmlFor={`goal-${goalIndex}-type`}>
        <FormattedMessage
          id="pages.scorecards.edit.goalType"
          defaultMessage="Goal Type"
        />
      </FieldLabel>
      <Controller
        name={`goals.${goalIndex}.type`}
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            onChange={(e) => handleTypeChange(e.target.value, field.onChange)}
            id={`goal-${goalIndex}-type`}
            fullWidth
            size="small"
            displayEmpty
            disabled={disabled}
            renderValue={(value) => {
              if (!value) return placeholder;
              const opt = goalTypeOptions.find((o) => o.id === value);
              return opt?.label ?? placeholder;
            }}
          >
            {goalTypeOptions.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                <Stack direction="row" alignItems="center" width="100%">
                  {opt.label}
                  <TooltipWithIcon title={opt.tooltip} disableInteractive>
                    <TooltipInfoIcon />
                  </TooltipWithIcon>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </FieldGroup>
  );
}
